import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { FEATURE_ACCESS_ATTRIBUTE_MAP } from 'config';
import { getIsLoggedIn } from 'ducks/auth/selectors';
import { fetchCurrentUserFeatureAccess } from 'services/featureAccess';

const EMPTY_FEATURE_ACCESS = Object.keys(FEATURE_ACCESS_ATTRIBUTE_MAP).reduce(
  (acc, key) => ({
    ...acc,
    [key]: false
  }),
  {}
);

const EMPTY_STATE = {
  organization: null,
  attributes: {},
  features: EMPTY_FEATURE_ACCESS,
  pending: false,
  error: null,
  user: null
};

let currentUserFeatureAccessCache = null;
let currentUserFeatureAccessPromise = null;

const clearCurrentUserFeatureAccessCache = () => {
  currentUserFeatureAccessCache = null;
  currentUserFeatureAccessPromise = null;
};

const getCurrentUserFeatureAccess = async (forceRefresh = false) => {
  if (!forceRefresh && currentUserFeatureAccessCache) {
    return currentUserFeatureAccessCache;
  }

  if (!forceRefresh && currentUserFeatureAccessPromise) {
    return currentUserFeatureAccessPromise;
  }

  currentUserFeatureAccessPromise = fetchCurrentUserFeatureAccess()
    .then(result => {
      currentUserFeatureAccessCache = result;
      currentUserFeatureAccessPromise = null;
      return result;
    })
    .catch(error => {
      currentUserFeatureAccessPromise = null;
      throw error;
    });

  return currentUserFeatureAccessPromise;
};

const useFeatureAccess = () => {
  const isLoggedIn = useSelector(getIsLoggedIn);
  const [state, setState] = useState(EMPTY_STATE);

  const refresh = useCallback(async (forceRefresh = true) => {
    if (!isLoggedIn) {
      clearCurrentUserFeatureAccessCache();
      setState(EMPTY_STATE);
      return EMPTY_STATE;
    }

    setState(prevState => ({
      ...prevState,
      pending: true,
      error: null
    }));

    try {
      const nextState = await getCurrentUserFeatureAccess(forceRefresh);
      const resolvedState = {
        ...nextState,
        pending: false,
        error: null
      };
      setState(resolvedState);
      return resolvedState;
    } catch (error) {
      const nextState = {
        ...EMPTY_STATE,
        pending: false,
        error: error?.message || 'Failed to load feature access.'
      };
      setState(nextState);
      return nextState;
    }
  }, [isLoggedIn]);

  useEffect(() => {
    refresh(false);
  }, [refresh]);

  return useMemo(
    () => ({
      organization: state.organization,
      attributes: state.attributes,
      features: state.features,
      pending: state.pending,
      error: state.error,
      refresh,
      user: state.user
    }),
    [
      refresh,
      state.attributes,
      state.error,
      state.features,
      state.organization,
      state.pending,
      state.user
    ]
  );
};

export default useFeatureAccess;
